import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { formatDate } from '@/shared/lib/dateUtils'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState, useEffect } from "react";
import { Users, Eye, X, CirclePlus, FileText, Save } from "lucide-react";
import { config } from '@/config/env';
import { filterOutMasterUsers } from '@/shared/lib/userFilters';

// Import UserAssignmentTable and styles
import UserAssignmentTable from '@/features/configuracion/components/UserAssignmentTable';
import "./AdminToggleStyles.css";
import { SwitchCustom } from '@/shared/components/Switch';
import toast from "react-hot-toast";

interface Module {
  id: string;
  name: string;
  description: string;
  version: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
}

interface UserAssignment {
  userId: string;
  moduleId: string;
  permissions: string[];
  isActive: boolean;
}

interface CreateModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (moduleData: {
    name: string;
    description: string;
    version: string;
    isActive: boolean;
  }) => void;
  loading: boolean;
}

const CreateModuleModal: React.FC<CreateModuleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    version: "1.0.0",
    isActive: true,
  });

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        name: "",
        description: "",
        version: "1.0.0",
        isActive: true,
      });
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <CapaDeModal>
      <div className="bg-[#ffffff] rounded-lg shadow-xl max-w-xl w-full mx-4">
        <div className="flex items-center justify-between p-6">
          <h3 className="text-xl font-semibold text-[#1E1E1F]">
            Crear Nuevo Módulo
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 h-[24px] w-[24px] bg-[#D9D9D9] rounded-full p-1 hover:bg-[#D9D9D9]/60"
            disabled={loading}
          >
            <X className="h-4 w-4" color="#7e7e94ff" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nombre del Módulo *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB]"
              placeholder="Ej. Gestión de Inventario"
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Descripción *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={5}
              className="w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB]"
              placeholder="Describe la funcionalidad del módulo..."
            />
          </div>

          <div className="bg-[#E8F8FD] py-3 px-4 rounded-md flex">
            <div className="h-[24px] w-auto mr-3">
              <FileText color="#116583" />
            </div>
            <p className="text-sm text-[#116583]">
              <strong>Nota:</strong> La versión se asignará automáticamente como
              "1.0.0" y el módulo se creará como activo por defecto.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                loading || !formData.name.trim() || !formData.description.trim()
              }
              className={estiloDeBoton('primario')}
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <Save className="h-4 w-4" />
              <span>{loading ? "Creando..." : "Crear Módulo"}</span>
            </button>
          </div>
        </form>
      </div>
    </CapaDeModal>
  );
};

const ModulesView: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  // Cargar módulos al montar el componente
  useEffect(() => {
    fetchModules();
    fetchUsers();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${config.apiBaseUrl}/modules`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setModules(data);
      } else {
        console.error("Error fetching modules:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${config.apiBaseUrl}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(filterOutMasterUsers(data.data || data));
      } else {
        console.error("Error fetching users:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchModuleAssignments = async (moduleId: string) => {
    try {
      setAssignmentLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${config.apiBaseUrl}/modules/${moduleId}/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserAssignments(data);
      } else {
        console.error(
          "Error fetching module assignments:",
          response.statusText
        );
        setUserAssignments([]);
      }
    } catch (error) {
      console.error("Error fetching module assignments:", error);
      setUserAssignments([]);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleModuleSelect = (module: Module) => {
    setSelectedModule(module);
    setShowAssignments(true);
    fetchModuleAssignments(module.id);
  };

  const handleAssignUser = async (userId: string) => {
    if (!selectedModule) return;

    try {
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${config.apiBaseUrl}/user-assignments/modules`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId,
            moduleId: selectedModule.id,
          }),
        }
      );

      if (response.ok) {
        fetchModuleAssignments(selectedModule.id);
        toast.success("Usuario asignado exitosamente al módulo");
      } else {
        const errorData = await response.text();
        console.error("Error assigning user:", response.statusText, errorData);
        toast.error(`Error al asignar usuario: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error assigning user:", error);
      toast.error(`Error de conexión: ${error}`);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selectedModule) return;

    try {
      const token = localStorage.getItem("access_token");

      // Using the working PATCH endpoint for user removal
      const response = await fetch(
        `${config.apiBaseUrl}/users/${userId}/modules/${selectedModule.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: false }),
        }
      );

      if (response.ok) {
        fetchModuleAssignments(selectedModule.id);
        toast.success("Usuario removido exitosamente del módulo");
      } else {
        const errorData = await response.text();
        console.error("Error removing user:", response.statusText, errorData);
        toast.error(`Error al remover usuario: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error(`Error de conexión: ${error}`);
    }
  };

  const handleModuleToggle = async (
    moduleId: string,
    currentStatus: boolean
  ) => {
    try {
      const token = localStorage.getItem("access_token");
      const newStatus = !currentStatus;

      const response = await fetch(`${config.apiBaseUrl}/modules/${moduleId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isActive: newStatus }),
      });

      if (response.ok) {
        fetchModules(); // Refresh the modules list
        toast.success(
          `Módulo ${newStatus ? "activado" : "desactivado"} exitosamente`
        );
      } else {
        const errorData = await response.text();
        console.error(
          "Error updating module status:",
          response.statusText,
          errorData
        );
        toast.error(
          `Error al cambiar el estado del módulo: ${response.statusText}`
        );
      }
    } catch (error) {
      console.error("Error toggling module status:", error);
      toast.error(`Error de conexión: ${error}`);
    }
  };

  const handleBackToModules = () => {
    setShowAssignments(false);
    setSelectedModule(null);
    setUserAssignments([]);
  };

  const handleCreateModule = async (moduleData: {
    name: string;
    description: string;
    version: string;
    isActive: boolean;
  }) => {
    try {
      setCreating(true);
      const token = localStorage.getItem("access_token");

      // Solo enviar los campos que acepta el backend
      const payload = {
        name: moduleData.name,
        description: moduleData.description,
      };

      const response = await fetch(`${config.apiBaseUrl}/modules`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const newModule = await response.json();
        setModules((prev) => [...prev, newModule]);
        setShowCreateModal(false);
        // Mostrar mensaje de éxito si tienes un sistema de notificaciones
      } else {
        // Try to parse as JSON first, fallback to text
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = await response.text();
        }
        console.error("Error creating module:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          requestData: payload,
        });
        // Mostrar mensaje de error
        toast.error(
          `Error al crear módulo: ${errorData.message || response.statusText}`
        );
      }
    } catch (error) {
      console.error("Error creating module:", error);
      toast.error(`Network error: ${error}`);
      // Mostrar mensaje de error
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="my-8">
      {!showAssignments ? (
        <>
          {/* Header */}
          <div className="flex justify-between items-center mb-8 ">
            <div className="flex items-center space-x-3">
              <h1 className="text-xl font-semibold text-[#1E1E1F]">
                Gestión de Módulos
              </h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className={estiloDeBoton('primario')}
            >
              <CirclePlus className="h-4 w-4" />
              <p className="text-sm">Nuevo Módulo</p>
            </button>
          </div>

          {/* Modules Table */}
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Módulos
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Versión
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Fecha Creación
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {modules.map((module) => (
                  <tr key={module.id} className="hover:bg-[#F4F7FB] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {module.name}
                        </div>
                        <div className="text-sm text-[#959595]">
                          {module.description}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {module.version ?? "-----"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <SwitchCustom
                          value={module.isActive}
                          onChange={() => {
                            handleModuleToggle(module.id, module.isActive);
                          }}
                        />
                        <p className="ml-2 text-sm">
                          {module.isActive ? "Activo" : "Inactivo"}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(module.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleModuleSelect(module)}
                        className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                      >
                        <Users className="h-4 w-4" />
                        <span>Usuarios</span>
                      </button>
                      <button className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold">
                        <Eye className="h-4 w-4" />
                        <span>Ver</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      ) : (
        <UserAssignmentTable
          title={`Usuarios del Módulo: ${selectedModule?.name}`}
          users={users}
          assignments={userAssignments}
          loading={assignmentLoading}
          onAssign={handleAssignUser}
          onRemove={handleRemoveUser}
          onBack={handleBackToModules}
          type="module"
        />
      )}

      {/* Create Module Modal */}
      <CreateModuleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateModule}
        loading={creating}
      />
    </div>
  );
};

export default ModulesView;
